import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { get, post } from "../api";
import { useI18n } from "../i18n";

interface UserProfile {
  email: string;
  email_verified: boolean;
  mfa_enabled?: boolean;
  workspaces: { id: string; name: string; capabilities: string[] }[];
}

interface MfaSetupResponse {
  secret: string;
  uri: string;
  recovery_codes: string[];
}

interface DeletePrecheck {
  can_delete: boolean;
  blocking_reasons: string[];
  blockers: {
    sole_owner_with_members: { workspace_id: string; name: string; member_count: number }[];
    active_paid_subscriptions: { workspace_id: string; plan_code: string; service_state: string }[];
  };
  cascading_workspaces: { workspace_id: string; name: string }[];
}

export function AccountSettingsPage({ onUpdated }: { onUpdated: () => void }) {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  // Password change state
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordMsg, setPasswordMsg] = useState<string | null>(null);
  const [passwordErr, setPasswordErr] = useState<string | null>(null);
  const [changingPassword, setChangingPassword] = useState(false);

  // MFA state
  const [mfaSetup, setMfaSetup] = useState<MfaSetupResponse | null>(null);
  const [confirmCode, setConfirmCode] = useState("");
  const [disablePassword, setDisablePassword] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [showDisableForm, setShowDisableForm] = useState(false);
  const [busy, setBusy] = useState(false);

  // Account deletion state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [precheck, setPrecheck] = useState<DeletePrecheck | null>(null);
  const [deletePassword, setDeletePassword] = useState("");
  const [deleteMfaCode, setDeleteMfaCode] = useState("");
  const [deleteErr, setDeleteErr] = useState<string | null>(null);

  const loadProfile = useCallback(async () => {
    try {
      const data = await get<UserProfile>("/api/v1/auth/me");
      setProfile(data);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  // Password change action
  async function handleChangePassword(e: React.FormEvent) {
    e.preventDefault();
    setPasswordErr(null);
    setPasswordMsg(null);
    if (!currentPassword) {
      setPasswordErr(t.account.currentPasswordLabel);
      return;
    }
    if (newPassword.length < 12) {
      setPasswordErr(t.account.newPasswordLabel);
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordErr(t.account.passwordMismatch);
      return;
    }
    if (newPassword === currentPassword) {
      setPasswordErr(t.account.passwordSameAsCurrent);
      return;
    }
    setChangingPassword(true);
    try {
      await post("/api/v1/auth/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      setPasswordMsg(t.account.changePasswordSuccess);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (e) {
      setPasswordErr((e as Error).message);
    } finally {
      setChangingPassword(false);
    }
  }

  // MFA Actions
  async function startMfaSetup() {
    setBusy(true); setErr(null); setMsg(null);
    try {
      const res = await post<MfaSetupResponse>("/api/v1/auth/mfa/setup");
      setMfaSetup(res);
      setConfirmCode("");
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function confirmMfa() {
    if (!confirmCode.trim()) return;
    setBusy(true); setErr(null); setMsg(null);
    try {
      await post("/api/v1/auth/mfa/confirm", { code: confirmCode.trim() });
      setMfaSetup(null);
      setMsg(locale === "zh" ? "两步验证 (MFA) 已成功启用！请妥善保存应急恢复码。" : "Two-factor authentication (MFA) enabled successfully! Please store recovery codes securely.");
      await loadProfile();
      onUpdated();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function disableMfa() {
    if (!disablePassword.trim()) return;
    setBusy(true); setErr(null); setMsg(null);
    try {
      await post("/api/v1/auth/mfa/disable", {
        password: disablePassword,
        code: disableCode.trim() || undefined,
      });
      setShowDisableForm(false);
      setDisablePassword("");
      setDisableCode("");
      setMsg(locale === "zh" ? "两步验证已停用。" : "Two-factor authentication disabled.");
      await loadProfile();
      onUpdated();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  // Account Deletion
  async function openDeletePrecheck() {
    setShowDeleteModal(true);
    setDeleteErr(null);
    setDeletePassword("");
    try {
      const res = await get<DeletePrecheck>("/api/v1/auth/delete-account/precheck");
      setPrecheck(res);
    } catch (e) {
      setDeleteErr((e as Error).message);
    }
  }

  async function executeAccountDeletion() {
    if (!deletePassword.trim()) {
      setDeleteErr(locale === "zh" ? "请输入登录密码以确认注销" : "Please enter your password to confirm.");
      return;
    }
    if (profile?.mfa_enabled && !deleteMfaCode.trim()) {
      setDeleteErr(locale === "zh" ? "请输入两步验证动态码或应急恢复码" : "Please enter your MFA code or recovery code.");
      return;
    }
    setBusy(true); setDeleteErr(null);
    try {
      await post("/api/v1/auth/delete-account", {
        password: deletePassword,
        mfa_code: deleteMfaCode.trim(),
        confirm: true,
      });
      alert(locale === "zh" ? "账号已注销。所有专属数据已被标记清理。" : "Account deleted. All personal resources marked for cleanup.");
      onUpdated();
      navigate("/");
    } catch (e) {
      setDeleteErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <div className="muted">{t.common.loading}</div>;
  if (!profile) return <div className="error">{err || t.common.error}</div>;

  return (
    <div>
      <div className="site-header-block">
        <div className="site-breadcrumbs">
          <Link to="/" className="site-breadcrumb-link">{t.common.workspaces}</Link>
          <span className="site-breadcrumb-sep">/</span>
          <span className="site-breadcrumb-current">{t.common.accountSecurity}</span>
        </div>
        <div className="site-title-bar">
          <div>
            <h1 className="site-main-title">{t.account.title}</h1>
            <p className="site-subtitle">
              {locale === "zh"
                ? "登录密码维护、两步动态验证 (MFA / TOTP) 及账户生命周期管理。"
                : "Manage login credentials, multi-factor authentication, and account lifecycle."}
            </p>
          </div>
        </div>
      </div>
      {msg && <p className="muted" style={{ padding: 10, background: "#f0fdf4", borderRadius: 8 }}>{msg}</p>}
      {err && <p className="error">{err}</p>}

      {/* Account Info */}
      <div className="card" style={{ marginBottom: 16 }}>
        <h3>{locale === "zh" ? "基本信息" : "Profile Information"}</h3>
        <p>{t.account.emailLabel}<b>{profile.email}</b> {profile.email_verified ? <span className="badge ok">{t.account.emailVerified}</span> : <span className="badge warn">{t.account.emailUnverified}</span>}</p>
        <p className="muted">{locale === "zh" ? `关联工作空间数量: ${profile.workspaces.length}` : `Associated workspaces: ${profile.workspaces.length}`}</p>
      </div>

      {/* Change Password */}
      <div className="card" style={{ marginBottom: 16 }}>
        <h3>{t.account.changePasswordTitle}</h3>
        <p className="muted">{t.account.changePasswordDesc}</p>
        {passwordMsg && <p className="muted" style={{ padding: 10, background: "#f0fdf4", color: "#166534", borderRadius: 8, marginBottom: 12 }}>{passwordMsg}</p>}
        {passwordErr && <p className="error" style={{ marginBottom: 12 }}>{passwordErr}</p>}
        <form onSubmit={handleChangePassword} style={{ maxWidth: 420 }}>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: "block", marginBottom: 4, fontWeight: 500 }}>{t.account.currentPasswordLabel}</label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="••••••••••••"
              style={{ width: "100%" }}
            />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: "block", marginBottom: 4, fontWeight: 500 }}>{t.account.newPasswordLabel}</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="••••••••••••"
              style={{ width: "100%" }}
            />
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", marginBottom: 4, fontWeight: 500 }}>{t.account.confirmNewPasswordLabel}</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="••••••••••••"
              style={{ width: "100%" }}
            />
          </div>
          <button
            type="submit"
            className="primary"
            disabled={changingPassword || !currentPassword || !newPassword || !confirmPassword}
          >
            {changingPassword ? t.common.submitting : t.account.changePasswordBtn}
          </button>
        </form>
      </div>

      {/* MFA Security */}
      <div className="card" style={{ marginBottom: 16 }}>
        <h3>{t.account.mfaSectionTitle}</h3>
        {profile.mfa_enabled ? (
          <div>
            <p><span className="badge ok">{t.account.mfaStatusEnabled}</span> {locale === "zh" ? "您的账号已受到 TOTP 两步验证保护。" : "Your account is protected with two-factor authentication."}</p>
            {!showDisableForm ? (
              <button className="ghost" onClick={() => setShowDisableForm(true)}>{t.account.mfaDisableBtn}</button>
            ) : (
              <div style={{ marginTop: 12, padding: 12, background: "#f8fafc", borderRadius: 8, maxWidth: 400 }}>
                <h4>{t.account.mfaDisableBtn}</h4>
                <label>{locale === "zh" ? "请输入登录密码:" : "Enter your password:"}</label>
                <input type="password" value={disablePassword} onChange={(e) => setDisablePassword(e.target.value)} />
                <label>{locale === "zh" ? "验证码或恢复码（可选）:" : "MFA or Recovery Code (optional):"}</label>
                <input value={disableCode} onChange={(e) => setDisableCode(e.target.value)} placeholder={t.auth.totpPlaceholder} />
                <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
                  <button className="primary" disabled={busy} onClick={disableMfa}>{t.common.submit}</button>
                  <button className="ghost" onClick={() => setShowDisableForm(false)}>{t.common.cancel}</button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div>
            <p className="muted">{locale === "zh" ? "启用两步验证可显著提升账号安全性。每次登录均需提供 Authenticator 生成的 6 位动态验证码。" : "Enhance account security by requiring an Authenticator 6-digit TOTP code on each sign-in."}</p>
            {!mfaSetup ? (
              <button className="primary" disabled={busy} onClick={startMfaSetup}>{t.account.mfaEnableBtn}</button>
            ) : (
              <div style={{ marginTop: 12, padding: 16, background: "#f8fafc", borderRadius: 8 }}>
                <h4>{locale === "zh" ? "配置 TOTP 验证器" : "Configure TOTP Authenticator"}</h4>
                <p>1. {t.account.mfaScanQr}</p>
                <code style={{ fontSize: 16, padding: "4px 8px", background: "#e2e8f0" }}>{mfaSetup.secret}</code>
                
                <p style={{ marginTop: 12 }}>2. {t.account.mfaRecoveryCodesTitle}</p>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, maxWidth: 360, background: "#fff", padding: 10, borderRadius: 6, border: "1px solid #cbd5e1" }}>
                  {mfaSetup.recovery_codes.map((c, i) => (
                    <code key={i}>{c}</code>
                  ))}
                </div>

                <div style={{ marginTop: 16 }}>
                  <label>3. {t.account.mfaConfirmCodeLabel}</label>
                  <div className="row" style={{ maxWidth: 320 }}>
                    <input value={confirmCode} onChange={(e) => setConfirmCode(e.target.value)} placeholder="123456" maxLength={6} />
                    <button className="primary" disabled={busy || !confirmCode.trim()} onClick={confirmMfa}>{t.account.mfaConfirmBtn}</button>
                    <button className="ghost" onClick={() => setMfaSetup(null)}>{t.common.cancel}</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Danger Zone */}
      <div className="card" style={{ borderColor: "#fca5a5" }}>
        <h3 style={{ color: "#b91c1c" }}>{t.account.deleteSectionTitle}</h3>
        <p className="muted">{t.account.deleteWarning}</p>
        <button style={{ background: "#dc2626", color: "#fff", border: "none", padding: "8px 16px", borderRadius: 6, cursor: "pointer" }}
                onClick={openDeletePrecheck}>
          {t.account.deleteBtn}
        </button>
      </div>

      {/* Account Deletion Modal */}
      {showDeleteModal && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: "rgba(0,0,0,0.5)", display: "flex",
          alignItems: "center", justifyContent: "center", zIndex: 1000
        }}>
          <div className="card" style={{ maxWidth: 500, width: "90%", background: "#fff" }}>
            <h3>{t.account.deleteSectionTitle}</h3>
            {deleteErr && <p className="error">{deleteErr}</p>}
            {!precheck ? (
              <p className="muted">{locale === "zh" ? "正在评估账号注销前置条件…" : "Evaluating precheck conditions…"}</p>
            ) : !precheck.can_delete ? (
              <div>
                <p className="error">{locale === "zh" ? "当前无法注销账号，存在以下阻塞项：" : "Cannot delete account due to existing blockers:"}</p>
                <ul>
                  {precheck.blocking_reasons.map((r, i) => (
                    <li key={i} style={{ color: "#b91c1c", marginBottom: 6 }}>{r}</li>
                  ))}
                </ul>
                <div style={{ marginTop: 16 }}>
                  <button className="primary" onClick={() => setShowDeleteModal(false)}>{t.common.back}</button>
                </div>
              </div>
            ) : (
              <div>
                <p>{locale === "zh" ? "前置校验通过。注销后：" : "Precheck passed. Upon deletion:"}</p>
                <ul>
                  <li>{locale === "zh" ? "您的登录凭证将被永久废弃。" : "All authentication sessions and tokens will be permanently revoked."}</li>
                  {precheck.cascading_workspaces.length > 0 && (
                    <li>{locale === "zh" ? "以下独资工作空间将同步进入注销清理流程：" : "Sole-owned workspaces will enter automated cleanup:"}
                      <b> {precheck.cascading_workspaces.map(w => w.name).join(", ")}</b>
                    </li>
                  )}
                  <li>{locale === "zh" ? "已加入的其他多成员团队将自动退出。" : "You will be removed from team workspaces."}</li>
                </ul>
                <label>{locale === "zh" ? "请输入登录密码以确认此操作：" : "Enter your password to confirm:"}</label>
                <input type="password" value={deletePassword}
                       onChange={(e) => setDeletePassword(e.target.value)}
                       placeholder={t.auth.passwordLabel} autoFocus />
                {profile.mfa_enabled && (
                  <div style={{ marginTop: 10 }}>
                    <label>{t.auth.totpLabel}</label>
                    <input value={deleteMfaCode}
                           onChange={(e) => setDeleteMfaCode(e.target.value)}
                           placeholder={t.auth.totpPlaceholder} />
                  </div>
                )}
                <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
                  <button style={{ background: "#dc2626", color: "#fff", border: "none", padding: "8px 16px", borderRadius: 6, cursor: "pointer" }}
                          disabled={busy} onClick={executeAccountDeletion}>
                    {busy ? t.common.submitting : t.account.deleteBtn}
                  </button>
                  <button className="ghost" onClick={() => setShowDeleteModal(false)}>{t.common.cancel}</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
